import {
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useId, useRef } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { HEADER_HEIGHT } from '../layouts/BaseLayout'

const OVERLAY_WIDTH = 520
const DEFAULT_WIDTH = 420

const SidePanel = ({
  title,
  children,
  open = false,
  onClose,
  overlay = false,
}) => {
  const theme = useTheme()
  const closeButtonRef = useRef(null)
  const previousFocusRef = useRef(null)
  const titleId = useId()

  useEffect(() => {
    if (open) {
      if (!overlay) {
        previousFocusRef.current = document.activeElement
      }
      const frame = requestAnimationFrame(() => {
        closeButtonRef.current?.focus()
      })
      return () => cancelAnimationFrame(frame)
    } else if (!overlay && previousFocusRef.current) {
      previousFocusRef.current.focus()
      previousFocusRef.current = null
    }
  }, [open, overlay])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant={overlay ? 'temporary' : 'persistent'}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': overlay ? true : undefined,
          'aria-labelledby': title ? titleId : undefined,
        },
      }}
      sx={{
        width: open
          ? { sm: '100%', md: overlay ? OVERLAY_WIDTH : DEFAULT_WIDTH }
          : 0,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: open
            ? theme.transitions.easing.easeOut
            : theme.transitions.easing.sharp,
          duration: open
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
        }),
        '& .MuiDrawer-paper': {
          width: { sm: '100%', md: overlay ? OVERLAY_WIDTH : DEFAULT_WIDTH },
          boxSizing: 'border-box',
          ...(!overlay && { top: HEADER_HEIGHT }),
        },
      }}
    >
      <Stack sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="close"
            ref={closeButtonRef}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {title && (
            <Typography variant="h6" id={titleId} component="h2">
              {title}
            </Typography>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {children}
      </Stack>
    </Drawer>
  )
}

export default SidePanel
